/*
 * review-photo-viewer.js
 *
 * Judge.me's review widget already opens review photos in its own full-size
 * viewer (`.jdgm-modal`), but that viewer ships without keyboard dismissal or
 * focus management. This asset adds only the missing accessibility behaviour:
 *
 *   - Escape closes the viewer (delegates to Judge.me's own close control).
 *   - Focus moves into the viewer when it opens.
 *   - Focus returns to the thumbnail that opened it when it closes.
 *
 * It relies on event delegation on `document` plus a MutationObserver on
 * `document.body`, so it survives Judge.me's async widget injection and the
 * re-renders caused by pagination, sorting and filtering.
 */
(function () {
  'use strict';

  var MODAL_SELECTOR = '.jdgm-modal';
  var CLOSE_SELECTORS = ['.jm-indicator__close', '[aria-label="Close"]', '[class*="__close"]'];
  var TRIGGER_SELECTOR = '.jm-media-thumbnail-list__thumbnail, .jm-media-grid__item, .jdgm-rev__pic-link, .jdgm-rev__pic-img, .photostrip__open';
  var DIALOG_LABEL = 'Review photo viewer';

  var lastTrigger = null;
  var openModal = null;

  function findModal() {
    // Prefer the node the observer saw opening; `.jdgm-modal` is a generic legacy
    // Judge.me class and document order is not a reliable proxy for "the open one".
    if (openModal && document.contains(openModal)) return openModal;
    var all = document.querySelectorAll(MODAL_SELECTOR);
    return all.length ? all[all.length - 1] : null;
  }

  function findClose(modal) {
    if (!modal) return null;
    for (var i = 0; i < CLOSE_SELECTORS.length; i++) {
      var candidate = modal.querySelector(CLOSE_SELECTORS[i]);
      if (candidate) return candidate;
    }
    return null;
  }

  function focusFallback(element) {
    if (!element) return;
    if (!element.hasAttribute('tabindex')) element.setAttribute('tabindex', '-1');
    element.focus();
  }

  function rememberTrigger(event) {
    // Keydown fires for navigation too; only an activation key actually opens the viewer.
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    var trigger = event.target && event.target.closest ? event.target.closest(TRIGGER_SELECTOR) : null;
    // Thumbnails inside the open viewer are navigation, not the element that opened it.
    if (!trigger || trigger.closest(MODAL_SELECTOR)) return;
    // A hidden trigger cannot be what the user activated: the AWG photo strip
    // forwards its click to Judge.me's hidden grid, and focus must return to
    // the visible strip tile, not the hidden grid button.
    if (!trigger.getClientRects().length) return;
    lastTrigger = trigger;
  }

  function handleOpen(modal) {
    openModal = modal;
    // Judge.me marks the viewer role="presentation", which strips the container's
    // semantics so it is not announced as a dialog.
    var role = modal.getAttribute('role');
    if (!role || role === 'presentation' || role === 'none') modal.setAttribute('role', 'dialog');
    // A role without a name is an `aria-dialog-name` violation: always set both.
    if (!modal.getAttribute('aria-label') && !modal.getAttribute('aria-labelledby')) {
      modal.setAttribute('aria-label', DIALOG_LABEL);
    }
    // Safe only because Judge.me's viewer traps Tab itself (verified 2026-09-22).
    // If that ever stops being true, drop aria-modal or add our own trap.
    if (!modal.getAttribute('aria-modal')) modal.setAttribute('aria-modal', 'true');

    var close = findClose(modal);
    // `typeof close.focus === 'function'` is true for every element and proves
    // nothing, so check where focus actually landed instead.
    if (close) close.focus();
    if (!modal.contains(document.activeElement)) focusFallback(modal);
  }

  function handleClose() {
    openModal = null;
    var target = lastTrigger && document.contains(lastTrigger) ? lastTrigger : null;
    if (target) target.focus();
    if (document.activeElement === document.body || !document.activeElement) {
      focusFallback(document.querySelector('.jdgm-review-widget'));
    }
    lastTrigger = null;
  }

  function onKeydown(event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    var modal = findModal();
    if (!modal) return;
    var close = findClose(modal);
    if (close) {
      event.preventDefault();
      close.click();
    } else {
      event.preventDefault();
      // Hide rather than remove: ripping the node out strands Judge.me's own
      // `body { overflow: hidden }` and leaves the page unscrollable.
      modal.setAttribute('hidden', '');
      document.body.style.overflow = '';
      handleClose();
    }
  }

  function observeBody() {
    if (!window.MutationObserver || !document.body) return;
    var observer = new window.MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var mutation = mutations[i];
        var added = mutation.addedNodes;
        for (var a = 0; a < added.length; a++) {
          var node = added[a];
          if (node.nodeType !== 1) continue;
          // Match the modal itself or a wrapper containing it, so one extra
          // level of Judge.me markup does not silently disable open handling.
          var found = node.matches && node.matches(MODAL_SELECTOR) ? node
            : (node.querySelector ? node.querySelector(MODAL_SELECTOR) : null);
          if (found) handleOpen(found);
        }
        var removed = mutation.removedNodes;
        for (var r = 0; r < removed.length; r++) {
          var gone = removed[r];
          if (gone.nodeType !== 1 || !openModal) continue;
          if (gone === openModal || (gone.contains && gone.contains(openModal))) handleClose();
        }
      }
    });
    observer.observe(document.body, { childList: true });
  }

  function init() {
    if (window.__wrReviewPhotoViewer) return;
    window.__wrReviewPhotoViewer = true;
    document.addEventListener('click', rememberTrigger, true);
    document.addEventListener('keydown', rememberTrigger, true);
    document.addEventListener('keydown', onKeydown, true);
    observeBody();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
